import { useEffect, useState } from 'react'

import { useFormContext, type FieldPath, type FieldValues, type PathValue } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Button, Sheet, SheetContent } from '@/components/ui'
import { FormControl, FormField, FormItem, FormMessage } from '@/components/ui/form'
import { VagaPadraoEscolhaOpcoes } from '@/features/proposta/brasil/components/vaga-padrao/vaga-padrao-escolha-opcoes'
import { VagaPadraoSelector } from '@/features/proposta/brasil/components/vaga-padrao/vaga-padrao-seletor'
import type { TSimulacaoPropostaBrasil } from '@/features/proposta/brasil/domain/schemas/simulacao-proposta-brasil.schema'
import type { TAcessorioDaUnidade } from '@/features/proposta/brasil/types/proposta.type'
import { usePropostaContext } from '@/features/proposta/shared/context/proposta-context'
import { useSuspenseProdutos } from '@/hooks/use-suspense-produtos'

import { VagaPadraoUnidadesVinculadas } from './vaga-padrao-unidades-vinculadas'

export const VagaPadraoTrigger = () => {
  const { t } = useTranslation('proposta')
  const { watch, control, setValue } = useFormContext<FieldValues>()
  const { idExternoBlocoPath } = usePropostaContext()

  const [open, setOpen] = useState(false)
  const idExternoBlocoUnidade = watch(idExternoBlocoPath) as number
  const { data: vagas } = useSuspenseProdutos(idExternoBlocoUnidade, 'Acessorio', true)

  const acessoriosDaUnidade = watch('informacoesIniciais.simulacaoUnidade.produto.acessoriosDaUnidade') as
    | TAcessorioDaUnidade[]
    | undefined
    | null

  const simulacoesAcessoriosExtras = watch('informacoesIniciais.simulacoesAcessorios') as TSimulacaoPropostaBrasil[]

  const obterEtapaDeSelecaoDeVaga = () => {
    if (acessoriosDaUnidade?.length) return 'acessoriosVinculadosUnidade'
    if (simulacoesAcessoriosExtras.length) return 'acessoriosExtrasNaProposta'
    return 'vagaPadrao'
  }

  const renderSheetContent = () => {
    const renderCase = obterEtapaDeSelecaoDeVaga()
    switch (renderCase) {
      case 'acessoriosVinculadosUnidade':
        return <VagaPadraoUnidadesVinculadas acessoriosVinculados={acessoriosDaUnidade!} />
      case 'acessoriosExtrasNaProposta': {
        const acessorios = simulacoesAcessoriosExtras.map((item) => item.produto)
        return (
          <VagaPadraoEscolhaOpcoes
            vagas={vagas}
            acessorios={acessorios}
            onConfirm={(selected) => {
              setValue('informacoesIniciais.vagaIds', selected, { shouldValidate: true })
              setOpen(false)
            }}
          />
        )
      }
      case 'vagaPadrao':
      default:
        return (
          <VagaPadraoSelector
            vagas={vagas}
            onConfirm={(selected) => {
              setValue('informacoesIniciais.vagaIds', selected as PathValue<FieldValues, FieldPath<FieldValues>>, {
                shouldValidate: true,
              })
              setOpen(false)
            }}
          />
        )
    }
  }

  useEffect(() => {
    const vagasFormatadas = acessoriosDaUnidade?.length
      ? acessoriosDaUnidade.flatMap((item) =>
          item.acessorios.map((acessorio) => ({
            id: acessorio.idExterno,
            nomeVaga: item.nome,
            descricao: acessorio.nome,
          })),
        )
      : []

    if (!vagasFormatadas?.length) return

    setValue('informacoesIniciais.vagaIds', vagasFormatadas)
  }, [setValue, acessoriosDaUnidade])

  return (
    <FormField
      control={control}
      name="informacoesIniciais.vagaIds"
      render={() => (
        <FormControl>
          <FormItem>
            <Sheet modal open={open} onOpenChange={setOpen}>
              <Button variant="default" onClick={() => setOpen(true)} className="w-full">
                {t('informacoes_iniciais.cards.vaga_padrao.vincule')}
              </Button>
              <SheetContent
                closeClassname="right-12"
                preventDismiss
                className="bg-foreground w-full max-w-4xl overflow-hidden sm:max-w-md md:max-w-2xl"
              >
                {open && renderSheetContent()}
              </SheetContent>
            </Sheet>
            <FormMessage />
          </FormItem>
        </FormControl>
      )}
    />
  )
}
